import { useEffect, useRef, useState } from 'react';
import { useT } from '../lib/i18n';

/**
 * Cloudflare Turnstile, in front of sign-up.
 *
 * Renders nothing at all when VITE_TURNSTILE_SITE_KEY is unset, which is the
 * dev and test case: the API's own check is open in that configuration too, so
 * the two halves agree rather than leaving a form that can never be submitted.
 *
 * The script is loaded on demand rather than in index.html. It is the only
 * third-party script this app has ever loaded, and loading it on every page —
 * including every page a child sees — to serve one form would be the wrong
 * trade in a product that otherwise touches no third party.
 */
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const humanCheckEnabled = !!SITE_KEY;

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SRC;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export function HumanCheck({ onToken }: { onToken: (token: string | null) => void }) {
  const { t } = useT();
  const box = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!SITE_KEY || !box.current) return;
    let widget: string | undefined;
    let alive = true;
    loadScript()
      .then(() => {
        if (!alive || !box.current || !window.turnstile) return;
        widget = window.turnstile.render(box.current, {
          sitekey: SITE_KEY,
          theme: 'auto',
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => setFailed(true),
        });
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (widget) window.turnstile?.remove(widget);
    };
    // onToken is stable at the call sites; re-rendering the widget on every
    // keystroke would reset the challenge under the person's hands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;
  return (
    <div className="human-check">
      <div ref={box} />
      {failed && (
        // The API fails open when Cloudflare is unreachable, so this is an
        // explanation rather than a dead end: the form still submits.
        <p className="small muted">{t('humanCheck.unavailable')}</p>
      )}
    </div>
  );
}
